import {Component, OnInit} from '@angular/core';
import {startOf, endOf, nowDate} from '../../../../../utilities/date-util';
import {FormControl, UntypedFormBuilder} from '@angular/forms';
import {
    Company,
    CompanyCategoryType,
    CompanyClient,
    PledgeBulkPaymentBatchParameters,
    PledgeBulkPaymentReportClient,
} from '../../../../../services/api.client';
import {NotificationService} from '../../../../../services/NotificationService';
import {BehaviorSubject, Observable} from 'rxjs';
import {Disposable} from '../../../../../utilities/Disposable';
import {debounceTime, takeUntil} from 'rxjs/operators';
import {Validators} from '../../../../../utilities/Validators';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';

(pdfMake as any).vfs = pdfFonts.pdfMake.vfs;

@Component({
    selector: 'app-pledge-bulk-payment-report',
    templateUrl: './pledge-generate-bulk-payment-report.component.html',
    styleUrls: ['./pledge-generate-bulk-payment-report.component.scss'],
    standalone: false
})
export class PledgeGenerateBulkPaymentReportComponent extends Disposable implements OnInit {
    public form = this.fb.group({
        startDate: [startOf(nowDate(), 'year'), Validators.compose([Validators.isDateValid])],
        endDate: [endOf(nowDate(), 'day'), Validators.compose([Validators.isDateValid])],
        companies: [],
        pledgeTypes: [],
    });

    public startDate = this.form.get('startDate') as FormControl;
    public endDate = this.form.get('endDate') as FormControl;
    public companyControl = this.form.get('companies') as FormControl;
    public pledgeTypes = this.form.get('pledgeTypes') as FormControl;
    public companies$: Observable<Company[]>;
    public companies: {id: string, name: string, category: string}[] = [];

    private pledgeCountSubject = new BehaviorSubject<number>(0);
    public pledgeCount$ = this.pledgeCountSubject.asObservable();
    public loading = true;

    groupStates: { [key: string]: boolean } = {}; // Tracks collapsed state per group

    constructor(private fb: UntypedFormBuilder,
                private pledgeReportClient: PledgeBulkPaymentReportClient,
                private _notificationService: NotificationService,
                private companyClient: CompanyClient) {
        super();
    }

    ngOnInit(): void {
        this.loading = true;

        const form = this.form.value;
        const filters = PledgeBulkPaymentBatchParameters.fromJS({
            startDate: form.startDate,
            endDate: form.endDate,
            pledgeTypes: form.pledgeTypes ? [form.pledgeTypes] : [],
            companies: form.companies || []
        });

       const request = this.companyClient.retrieveCompaniesPaged(
            null,
            null,
            false,
            null,
            10000,
            0,
            'Name',
            null,
        );
        request.subscribe({
            next: (result) => {
                if (!!result?.items) {
                    this.companies = result?.items
                        .filter(x => x.name && x.name?.trim() !== '')
                        .map(company => ({
                        id: company.id,
                        name: company.name,
                        category: this.getCompanyCategoryLabel(company.category)
                    }));

                    this.companies.forEach(item => {
                        if (!this.groupStates[item.category]) {
                            this.groupStates[item.category] = true; // Collapsed by default
                        }
                    });
                }
            }, error: () => {
                this._notificationService.showError('Error Loading Companies');
            }, complete: () => {

            }
        });

        const getPledgeCount$ = this.pledgeReportClient.getPledgeCount(filters);

        getPledgeCount$.subscribe(
            pledgeCount => {
                this.pledgeCountSubject.next(pledgeCount);
                this.loading = false;
            },
            error => {
                this.loading = false;
                this._notificationService.showError('An error occurred. Please try again later.');
            }
        );

        this.onFormChanges();
    }

    get companiesArray(): string[] {
        return this.form.get('companies').value || [];
    }

    indexOf(item: any): number {
        return this.companiesArray.findIndex(x => this.compareItems(x, item));
    }

    getAdditionalItemsText(): string {
        const additionalCount = this.companiesArray.length - 5;
        return additionalCount > 0 ? `+ ${additionalCount} more...` : '';
    }

    compareItems = (item, selected) => {
        if (!!item && selected?.id) {
            return item === selected.id;
        }
        return false;
    };

    private getCompanyCategoryLabel(categoryType: CompanyCategoryType | string) {
        switch (categoryType) {
            case 'Utility':
                return 'Utility';
            case 'CarRepair':
                return 'Car Repair';
            case 'ChildcareSupport':
                return 'Childcare Support';
            case 'EmergencyRentalAssistance':
                return 'Emergency Rental Assistance';
            case 'TransportationForWorkforceNeeds':
                return 'Transportation for Workforce Needs';
            default:
                return 'Unknown';
        }
    }

    customSearchFn(term: string, item: any) {
        term = term.toLowerCase();
        return item.name.toLowerCase().includes(term) || item.category.toLowerCase().includes(term);
    }

    private onFormChanges(): void {
        this.form.valueChanges.pipe(
            takeUntil(this.$destroyed),
            debounceTime(1000)
        ).subscribe(x => {
            if (this.startDate.invalid || this.endDate.invalid) {
                return;
            }
            const form = this.form.value;
            const filters = PledgeBulkPaymentBatchParameters.fromJS({
                startDate: form.startDate,
                endDate: form.endDate,
                pledgeTypes: form.pledgeTypes ? form.pledgeTypes : [],
                companies: form.companies || []
            });
            this.pledgeReportClient.getPledgeCount(filters).subscribe(count => {
                this.pledgeCountSubject.next(count);
            }, err => {
                this._notificationService.showError('An error occurred while retrieving pledge count. Please refresh page and try again.');
            });
        });
    }

    public createBatch(): void {
        this.loading = true;
        const form = this.form.value;
        const filters = PledgeBulkPaymentBatchParameters.fromJS({
            startDate: form.startDate,
            endDate: form.endDate,
            pledgeTypes: form.pledgeTypes ? form.pledgeTypes : [],
            companies: form.companies || []
        });

        this.pledgeReportClient.createBulkPaymentBatch(filters).subscribe(
            response => {
                this._notificationService.showSuccess(`Bulk payment batch ${response.batchId} with ${response.pledgeCount} pledges for $${response.totalAmount.toFixed(2)} created successfully.`);
                this.resetForm();
                this.loading = false;
            },
            err => {
                this.loading = false;
                this._notificationService.showError(`An error occurred while creating the batch ${err.value}. Please try again.`);
            }
        );
    }

    private resetForm(): void {
        this.form.patchValue({
            startDate: startOf(nowDate(), 'year'),
            endDate: endOf(nowDate(), 'day'),
            companies: [],
            pledgeTypes: [],
        });
    }

}
